import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';

export interface OverdueUpdatesCellProps {
  /** `null` when the coach has no success-update data (leads no active clients). */
  overdue: number | null;
  active: number | null;
  awaiting: number | null;
  /** The coach's display name, for the button's accessible label. */
  name: string;
  onOpen: () => void;
}

const OVERDUE_TEXT = '#92400E';
const BAR_FILL = '#D97706';
const BAR_TRACK = '#F3F4F6';
const UP_TO_DATE_TEXT = '#047857';
const BAR_WIDTH = 120;

/**
 * Overdue clients for one coach: the count out of their active clients, a share bar, and any
 * clients still awaiting a first update. Opens the client list; clean coaches get plain text.
 */
export const OverdueUpdatesCell: React.FC<OverdueUpdatesCellProps> = ({ overdue, active, awaiting, name, onOpen }) => {
  if (active === null) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No active clients
      </Typography>
    );
  }

  const overdueCount = overdue ?? 0;
  const awaitingCount = awaiting ?? 0;
  if (overdueCount + awaitingCount === 0) {
    return (
      <Typography variant="body2" sx={{ color: UP_TO_DATE_TEXT, fontWeight: 600 }}>
        All up to date
      </Typography>
    );
  }

  const share = active > 0 ? Math.min(100, (overdueCount / active) * 100) : 0;

  return (
    <ButtonBase
      aria-label={`${name}: ${overdueCount} of ${active} active clients overdue${awaitingCount > 0 ? `, ${awaitingCount} awaiting a first update` : ''}. Show clients`}
      onClick={(event) => {
        // The row opens the staff page; this opens the client list instead.
        event.stopPropagation();
        onOpen();
      }}
      onKeyDown={(event) => {
        // The grid treats Space as "page down" and cancels it, which would stop the button activating.
        if (event.key === ' ') event.stopPropagation();
      }}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.25,
        px: 0.75,
        py: 0.5,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
        '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
      })}
    >
      <Box component="span">
        <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: OVERDUE_TEXT }}>
          {overdueCount}
        </Typography>{' '}
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
          of {active}
        </Typography>
      </Box>
      <Box
        component="span"
        aria-hidden="true"
        sx={{ display: 'block', width: BAR_WIDTH, height: 6, borderRadius: 3, bgcolor: BAR_TRACK, overflow: 'hidden' }}
      >
        <Box component="span" sx={{ display: 'block', height: '100%', bgcolor: BAR_FILL }} style={{ width: `${share}%` }} />
      </Box>
      {awaitingCount > 0 ? (
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
          +{awaitingCount} awaiting first
        </Typography>
      ) : null}
    </ButtonBase>
  );
};
